/**
 * @file DevicePreviewContent.tsx
 * @description
 *   Main content for the Device Preview tab. Renders modal, device list,
 *   screen, and active session table. Consumes RDP, session, and navigation contexts.
 */

import { useCallback, useMemo, useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { DeviceList, DeviceScreen, useRDP } from '@amzn/noolpaavai-react';
import { CMS, PageId, PageType, Slot } from '@amzn/fire-tv-campaign-automation-service';
import {
  InvalidInputException,
  InternalServerException,
  ResourceNotFoundException,
} from '@amzn/fire-tv-campaign-automation-service';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { useSelection } from 'src/contexts/SelectionContext';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { initiatePreview, terminatePreview } from 'src/utils/clients/controlPlane/apiMethods';
import { logger } from 'src/logger';
import { useToast } from 'src/contexts/ToastContext';

import { PreviewSessionStatus } from './types';
import { usePreviewSession } from './PreviewSessionContext';
import { useDevicePreviewNavigation } from './DevicePreviewNavigationContext';
import { PreviewSessionTable } from './PreviewSessionTable';
import { DevicePreviewModal, DeviceOption, StartPreviewParams } from './DevicePreviewModal';

export const DevicePreviewContent = () => {
  const { rdpState } = useRDP();
  const {
    sessions,
    addSession,
    removeSession,
    updateSessionStatus,
    getOtherSessionForCustomerByDevice,
    getSession,
  } = usePreviewSession();
  const { isTestCampaignModalOpen, closeTestCampaignModal } = useDevicePreviewNavigation();
  const { marketplace, deviceFamily } = useSelection();
  const { user } = useAuthentication();
  const [isStarting, setIsStarting] = useState(false);
  const controlPlaneClient = getControlPlaneClient();
  const userAlias = user ? getUserAlias(user) : '';
  const { addToast } = useToast();

  const deviceOptions: DeviceOption[] = useMemo(
    () =>
      rdpState.connectedDevices
        .filter((d) => d.deviceId)
        .map((d) => ({
          name: d.deviceId!,
          value: d.deviceId!,
          customerId: d.customerId ?? undefined,
        })),
    [rdpState.connectedDevices],
  );

  const handleStartPreview = useCallback(
    async (params: StartPreviewParams) => {
      setIsStarting(true);
      try {
        const response = await initiatePreview(controlPlaneClient, {
          requestContext: { marketPlace: marketplace, deviceFamily, actor: userAlias },
          customerId: params.customerId,
          deviceId: params.deviceId || undefined,
          previewContent: {
            cms: CMS.CONTENT_SYMPHONY,
            creativeId: params.creativeId,
            pageId: params.pageId as PageId,
            pageType: params.pageType as PageType,
            slot: params.slot as Slot,
            marketplace,
          },
          durationInSeconds: params.durationInSeconds,
        });
        const expiresAt = new Date(response.expiresAt).getTime();
        addSession({
          deviceId: params.deviceId,
          customerId: params.customerId,
          creativeId: params.creativeId,
          expiresAt,
        });
        closeTestCampaignModal();
        addToast({ description: 'Preview started successfully', variant: 'success' });
      } catch (err) {
        logger.error('InitiatePreview failed', { error: err });
        let description = 'Failed to initiate preview';
        if (err instanceof InvalidInputException) {
          description = `Invalid input: ${err.message}`;
        } else if (err instanceof ResourceNotFoundException) {
          description = `Resource not found: ${err.message}`;
        } else if (err instanceof InternalServerException) {
          description = 'Server error. Please try again later.';
        } else if (err instanceof Error) {
          description = err.message;
        }
        addToast({ description, variant: 'danger' });
      } finally {
        setIsStarting(false);
      }
    },
    [
      controlPlaneClient,
      marketplace,
      deviceFamily,
      userAlias,
      addSession,
      addToast,
      closeTestCampaignModal,
    ],
  );

  const handleTerminatePreview = useCallback(
    async (deviceId: string) => {
      const session = getSession(deviceId);
      if (!session) return;

      updateSessionStatus(deviceId, PreviewSessionStatus.TERMINATING);
      try {
        const others = getOtherSessionForCustomerByDevice(deviceId);
        if (others.length === 0) {
          await terminatePreview(controlPlaneClient, {
            requestContext: { marketPlace: marketplace, deviceFamily, actor: userAlias },
            cms: CMS.CONTENT_SYMPHONY,
            sessionId: session.customerId,
          });
        }
        removeSession(deviceId);
      } catch (err) {
        logger.error('TerminatePreview failed', { error: err });
        let description = 'Failed to terminate preview';
        if (err instanceof InvalidInputException) {
          description = `Invalid input: ${err.message}`;
        } else if (err instanceof ResourceNotFoundException) {
          description = `Session not found: ${err.message}`;
        } else if (err instanceof InternalServerException) {
          description = 'Server error. Please try again later.';
        } else if (err instanceof Error) {
          description = err.message;
        }
        addToast({ description, variant: 'danger' });
        updateSessionStatus(deviceId, PreviewSessionStatus.ACTIVE);
      }
    },
    [
      getSession,
      removeSession,
      updateSessionStatus,
      getOtherSessionForCustomerByDevice,
      controlPlaneClient,
      marketplace,
      deviceFamily,
      userAlias,
      addToast,
    ],
  );

  return (
    <SpaceBetween direction="vertical" size="l">
      <DevicePreviewModal
        isVisible={isTestCampaignModalOpen}
        onClose={closeTestCampaignModal}
        onStartPreview={handleStartPreview}
        isLoading={isStarting}
        deviceOptions={deviceOptions}
      />
      <DeviceList />
      <DeviceScreen />
      <PreviewSessionTable sessions={sessions} onTerminatePreview={handleTerminatePreview} />
    </SpaceBetween>
  );
};
